import React, { useEffect, useState } from "react";
import { clearToken, isAdminWithToken, getUserNameWithToken } from "../../../utils/storage/Token";
import { getIdWithToken } from "../../../utils/storage/Token";
import { useFetchConfigUser } from "../../../service/AccountService";
import { Md5 } from "ts-md5";
import "./HeaderDashboard.scss";
import { PostRequest } from "../../../hook/useRequest";
import { getTheme, toggleTheme } from "../../../utils/storage/Theme";

type HeaderDashboardProps = {
    setMenuSelected: (menu: string) => void;
    menuSelected: string;
};

const HeaderDashboard: React.FunctionComponent<HeaderDashboardProps> = ({ setMenuSelected, menuSelected }) => {
    const { data: configUser } = useFetchConfigUser(getIdWithToken());
    const [avatarUrl, setAvatarUrl] = useState("00000000000000000000000000000000");
    const [hamburgerToggle, setHamburgerToggle] = useState(false);
    const [openAccordion, setOpenAccordion] = useState<string | null>(null);
    const [theme, setThemeState] = useState(getTheme());

    useEffect(() => {
        if (configUser) {
            const md5 = new Md5();
            const mail = configUser.user.userInfo.userSMTP;
            md5.appendStr(mail);
            let hashMail = "00000000000000000000000000000000";
            let ret = md5.end() as string;
            if (ret) {
                hashMail = ret;
            }
            setAvatarUrl(hashMail);
        }
    }, [configUser]);

    const onClick = async (typeMenu: string) => {
        if (typeMenu === "logout") {
            await PostRequest("/authentication/logout", {})
                .then(() => {
                    clearToken();
                    window.location.reload();
                })
                .catch((error) => console.log(error));
        } else if (typeMenu === "card") {
            window.open(`/user/${getUserNameWithToken()}`, "_blank");
        } else {
            setMenuSelected(typeMenu);
        }
        setHamburgerToggle(false);
        setOpenAccordion(null);
    };

    const toggleHamburger = () => {
        setHamburgerToggle(!hamburgerToggle);
        if (hamburgerToggle) setOpenAccordion(null);
    };

    const toggleAccordion = (name: string) => {
        setOpenAccordion(openAccordion === name ? null : name);
    };

    const handleToggleTheme = () => {
        const next = toggleTheme();
        setThemeState(next);
    };

    const isActive = (menu: string) => menuSelected === menu;
    const isGroupActive = (menus: string[]) => menus.some((m) => menuSelected === m);

    return (
        <>
            {/* ============ SIDEBAR DESKTOP ============ */}
            <aside className="sidebar">
                <div className="sidebarLogo" onClick={() => onClick("")}>
                    <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="logo_fc" height={32} width={32} />
                    <span className="sidebarLogoText">freeCompta</span>
                </div>

                <nav className="sidebarNav">
                    <div className={`sidebarItem ${menuSelected === "" ? "active" : ""}`} onClick={() => onClick("")}>
                        <span className="material-symbols-outlined sidebarIcon">dashboard</span>
                        <span>Dashboard</span>
                    </div>

                    {/* Business dropdown */}
                    <div
                        className={`sidebarGroup ${
                            isGroupActive(["customer", "businessDeal", "billing", "outlay", "explorer"])
                                ? "groupActive"
                                : ""
                        }`}
                    >
                        <div className="sidebarItem sidebarItemDropdown" onClick={() => toggleAccordion("business")}>
                            <span className="material-symbols-outlined sidebarIcon">business_center</span>
                            <span>Business</span>
                            <span className={`sidebarArrow ${openAccordion === "business" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "business" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("customer") ? "active" : ""}`}
                                onClick={() => onClick("customer")}
                            >
                                Clients
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("businessDeal") ? "active" : ""}`}
                                onClick={() => onClick("businessDeal")}
                            >
                                Contrats
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("billing") ? "active" : ""}`}
                                onClick={() => onClick("billing")}
                            >
                                Facturations
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("outlay") ? "active" : ""}`}
                                onClick={() => onClick("outlay")}
                            >
                                Frais de déplacement
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("explorer") ? "active" : ""}`}
                                onClick={() => onClick("explorer")}
                            >
                                Mes fichiers
                            </div>
                        </div>
                    </div>

                    {/* Déclarations dropdown */}
                    <div
                        className={`sidebarGroup ${
                            isGroupActive(["resume_dec", "urssaf", "tva", "help_declaration"]) ? "groupActive" : ""
                        }`}
                    >
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("declarations")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">description</span>
                            <span>Déclarations</span>
                            <span className={`sidebarArrow ${openAccordion === "declarations" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "declarations" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("resume_dec") ? "active" : ""}`}
                                onClick={() => onClick("resume_dec")}
                            >
                                Résumé
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("urssaf") ? "active" : ""}`}
                                onClick={() => onClick("urssaf")}
                            >
                                URSSAF
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("tva") ? "active" : ""}`}
                                onClick={() => onClick("tva")}
                            >
                                TVA
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("help_declaration") ? "active" : ""}`}
                                onClick={() => onClick("help_declaration")}
                            >
                                Aide
                            </div>
                        </div>
                    </div>

                    <div
                        className={`sidebarItem ${isActive("calendar") ? "active" : ""}`}
                        onClick={() => onClick("calendar")}
                    >
                        <span className="material-symbols-outlined sidebarIcon">calendar_month</span>
                        <span>Planning</span>
                    </div>

                    <div
                        className={`sidebarItem ${isActive("address") ? "active" : ""}`}
                        onClick={() => onClick("address")}
                    >
                        <span className="material-symbols-outlined sidebarIcon">contacts</span>
                        <span>Adresses</span>
                    </div>

                    {/* Statistiques dropdown */}
                    <div
                        className={`sidebarGroup ${isGroupActive(["stat_urssaf", "stat_annual"]) ? "groupActive" : ""}`}
                    >
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("statistiques")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">bar_chart</span>
                            <span>Statistiques</span>
                            <span className={`sidebarArrow ${openAccordion === "statistiques" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "statistiques" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("stat_urssaf") ? "active" : ""}`}
                                onClick={() => onClick("stat_urssaf")}
                            >
                                Cumul URSSAF
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("stat_annual") ? "active" : ""}`}
                                onClick={() => onClick("stat_annual")}
                            >
                                Cumul Annuel
                            </div>
                        </div>
                    </div>

                    {/* Attestations dropdown */}
                    <div className="sidebarGroup">
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("attestations")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">verified</span>
                            <span>Attestations</span>
                            <span className={`sidebarArrow ${openAccordion === "attestations" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "attestations" ? "open" : ""}`}>
                            <div
                                className="sidebarSubItem"
                                onClick={() =>
                                    window.open(
                                        `https://api-avis-situation-sirene.insee.fr/identification/pdf/${configUser?.user?.userInfo?.companySiret?.replaceAll(
                                            " ",
                                            "",
                                        )}`,
                                        "_blank",
                                    )
                                }
                            >
                                Avis SIRENE
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/0`, "_blank")}
                            >
                                Non assujettissement TVA
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/1`, "_blank")}
                            >
                                Non emplois trav. étranger
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/2`, "_blank")}
                            >
                                Marché Public/Privé
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/3`, "_blank")}
                            >
                                Jours enfant malade
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Bottom section */}
                <div className="sidebarBottom">
                    <div
                        className="sidebarItem"
                        onClick={handleToggleTheme}
                        title={theme === "dark" ? "Mode clair" : "Mode sombre"}
                    >
                        <span className="material-symbols-outlined sidebarIcon">
                            {theme === "dark" ? "light_mode" : "dark_mode"}
                        </span>
                        <span>{theme === "dark" ? "Mode clair" : "Mode sombre"}</span>
                    </div>

                    <a className="sidebarItem" href="mailto:admin@free-compta.fr">
                        <span className="material-symbols-outlined sidebarIcon">mail</span>
                        <span>Contact</span>
                    </a>

                    {/* Paramètres / Compte */}
                    <div
                        className={`sidebarGroup ${
                            isGroupActive(["config", "password", "apikeys"]) ? "groupActive" : ""
                        }`}
                    >
                        <div className="sidebarItem sidebarItemDropdown" onClick={() => toggleAccordion("compte")}>
                            <span className="material-symbols-outlined sidebarIcon">settings</span>
                            <span>Paramètres</span>
                            <span className={`sidebarArrow ${openAccordion === "compte" ? "open" : ""}`}>&#9662;</span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "compte" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("config") ? "active" : ""}`}
                                onClick={() => onClick("config")}
                            >
                                Configuration
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("password") ? "active" : ""}`}
                                onClick={() => onClick("password")}
                            >
                                Mot de passe
                            </div>
                            <div className="sidebarSubItem" onClick={() => onClick("card")}>
                                Carte de visite
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("apikeys") ? "active" : ""}`}
                                onClick={() => onClick("apikeys")}
                            >
                                Clés API MCP
                            </div>
                            {isAdminWithToken() && (
                                <div
                                    className={`sidebarSubItem ${isActive("adminusers") ? "active" : ""}`}
                                    onClick={() => onClick("adminusers")}
                                >
                                    Administration
                                </div>
                            )}
                            <div className="sidebarSubItem sidebarSubItemLogout" onClick={() => onClick("logout")}>
                                Déconnexion
                            </div>
                        </div>
                    </div>
                </div>
            </aside>

            {/* ============ MOBILE HEADER ============ */}
            <header className="mobileHeader">
                <div className="mobileHeaderLeft">
                    <div className="menuToggle" onClick={toggleHamburger}>
                        <span className="iconBar"></span>
                        <span className="iconBar"></span>
                        <span className="iconBar"></span>
                    </div>
                </div>
                <div className="mobileHeaderCenter" onClick={() => onClick("")}>
                    <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="logo_fc" height={28} width={28} />
                    <span className="mobileHeaderTitle">freeCompta</span>
                </div>
                <div className="mobileHeaderRight">
                    <img className="mobileAvatar" src={"https://www.gravatar.com/avatar/" + avatarUrl} alt="gravatar" />
                </div>
            </header>

            {/* ============ MOBILE SIDEBAR (slide-in) ============ */}
            {hamburgerToggle && <div className="menuOverlay show" onClick={() => setHamburgerToggle(false)}></div>}
            <div className={`mobileSidebar ${hamburgerToggle ? "open" : ""}`}>
                <div className="mobileSidebarHeader">
                    <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="logo_fc" height={28} width={28} />
                    <span className="mobileSidebarTitle">freeCompta</span>
                </div>

                <nav className="mobileSidebarNav">
                    <div className={`sidebarItem ${menuSelected === "" ? "active" : ""}`} onClick={() => onClick("")}>
                        <span className="material-symbols-outlined sidebarIcon">dashboard</span>
                        <span>Dashboard</span>
                    </div>

                    <div className="sidebarGroup">
                        <div className="sidebarItem sidebarItemDropdown" onClick={() => toggleAccordion("m_business")}>
                            <span className="material-symbols-outlined sidebarIcon">business_center</span>
                            <span>Business</span>
                            <span className={`sidebarArrow ${openAccordion === "m_business" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "m_business" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("customer") ? "active" : ""}`}
                                onClick={() => onClick("customer")}
                            >
                                Clients
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("businessDeal") ? "active" : ""}`}
                                onClick={() => onClick("businessDeal")}
                            >
                                Contrats
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("billing") ? "active" : ""}`}
                                onClick={() => onClick("billing")}
                            >
                                Facturations
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("outlay") ? "active" : ""}`}
                                onClick={() => onClick("outlay")}
                            >
                                Frais de déplacement
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("explorer") ? "active" : ""}`}
                                onClick={() => onClick("explorer")}
                            >
                                Mes fichiers
                            </div>
                        </div>
                    </div>

                    <div className="sidebarGroup">
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("m_declarations")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">description</span>
                            <span>Déclarations</span>
                            <span className={`sidebarArrow ${openAccordion === "m_declarations" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "m_declarations" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("resume_dec") ? "active" : ""}`}
                                onClick={() => onClick("resume_dec")}
                            >
                                Résumé
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("urssaf") ? "active" : ""}`}
                                onClick={() => onClick("urssaf")}
                            >
                                URSSAF
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("tva") ? "active" : ""}`}
                                onClick={() => onClick("tva")}
                            >
                                TVA
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("help_declaration") ? "active" : ""}`}
                                onClick={() => onClick("help_declaration")}
                            >
                                Aide
                            </div>
                        </div>
                    </div>

                    <div
                        className={`sidebarItem ${isActive("calendar") ? "active" : ""}`}
                        onClick={() => onClick("calendar")}
                    >
                        <span className="material-symbols-outlined sidebarIcon">calendar_month</span>
                        <span>Planning</span>
                    </div>

                    <div
                        className={`sidebarItem ${isActive("address") ? "active" : ""}`}
                        onClick={() => onClick("address")}
                    >
                        <span className="material-symbols-outlined sidebarIcon">contacts</span>
                        <span>Adresses</span>
                    </div>

                    <div className="sidebarGroup">
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("m_statistiques")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">bar_chart</span>
                            <span>Statistiques</span>
                            <span className={`sidebarArrow ${openAccordion === "m_statistiques" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "m_statistiques" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("stat_urssaf") ? "active" : ""}`}
                                onClick={() => onClick("stat_urssaf")}
                            >
                                Cumul URSSAF
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("stat_annual") ? "active" : ""}`}
                                onClick={() => onClick("stat_annual")}
                            >
                                Cumul Annuel
                            </div>
                        </div>
                    </div>

                    <div className="sidebarGroup">
                        <div
                            className="sidebarItem sidebarItemDropdown"
                            onClick={() => toggleAccordion("m_attestations")}
                        >
                            <span className="material-symbols-outlined sidebarIcon">verified</span>
                            <span>Attestations</span>
                            <span className={`sidebarArrow ${openAccordion === "m_attestations" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "m_attestations" ? "open" : ""}`}>
                            <div
                                className="sidebarSubItem"
                                onClick={() =>
                                    window.open(
                                        `https://api-avis-situation-sirene.insee.fr/identification/pdf/${configUser?.user?.userInfo?.companySiret?.replaceAll(
                                            " ",
                                            "",
                                        )}`,
                                        "_blank",
                                    )
                                }
                            >
                                Avis SIRENE
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/0`, "_blank")}
                            >
                                Non assujettissement TVA
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/1`, "_blank")}
                            >
                                Non emplois trav. étranger
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/2`, "_blank")}
                            >
                                Marché Public/Privé
                            </div>
                            <div
                                className="sidebarSubItem"
                                onClick={() => window.open(`${process.env.PUBLIC_URL}/pdf/ash/3`, "_blank")}
                            >
                                Jours enfant malade
                            </div>
                        </div>
                    </div>

                    <div className="sidebarItem" onClick={handleToggleTheme}>
                        <span className="material-symbols-outlined sidebarIcon">
                            {theme === "dark" ? "light_mode" : "dark_mode"}
                        </span>
                        <span>{theme === "dark" ? "Mode clair" : "Mode sombre"}</span>
                    </div>

                    <a className="sidebarItem" href="mailto:admin@free-compta.fr">
                        <span className="material-symbols-outlined sidebarIcon">mail</span>
                        <span>Contact</span>
                    </a>

                    <div className="sidebarGroup">
                        <div className="sidebarItem sidebarItemDropdown" onClick={() => toggleAccordion("m_compte")}>
                            <span className="material-symbols-outlined sidebarIcon">settings</span>
                            <span>Paramètres</span>
                            <span className={`sidebarArrow ${openAccordion === "m_compte" ? "open" : ""}`}>
                                &#9662;
                            </span>
                        </div>
                        <div className={`sidebarDropdown ${openAccordion === "m_compte" ? "open" : ""}`}>
                            <div
                                className={`sidebarSubItem ${isActive("config") ? "active" : ""}`}
                                onClick={() => onClick("config")}
                            >
                                Configuration
                            </div>
                            <div
                                className={`sidebarSubItem ${isActive("password") ? "active" : ""}`}
                                onClick={() => onClick("password")}
                            >
                                Mot de passe
                            </div>
                            <div className="sidebarSubItem" onClick={() => onClick("card")}>
                                Carte de visite
                            </div>
                            {isAdminWithToken() && (
                                <div
                                    className={`sidebarSubItem ${isActive("adminusers") ? "active" : ""}`}
                                    onClick={() => onClick("adminusers")}
                                >
                                    Administration
                                </div>
                            )}
                            <div className="sidebarSubItem sidebarSubItemLogout" onClick={() => onClick("logout")}>
                                Déconnexion
                            </div>
                        </div>
                    </div>
                </nav>
            </div>
        </>
    );
};

export default HeaderDashboard;
